<template>
  <div class="tools-calendar">
    <div class="tool-title">
      Calendário
    </div>

    <div v-if="loading" class="text-center p-5">
      <b-spinner variant="primary" />
    </div>

    <div v-else-if="error" class="error p-4 text-center">
      Erro!
      <button class="btn-link link bg-transparent p-0 border-0" @click="load">
        Tentar novamente
      </button>
    </div>

    <div v-show="!loading && !error" class="row">
      <div class="col-lg-8 py-4">
        <v-calendar
          class="w-100"
          is-expanded
          title-position="right"
          :attributes="attrs"
          @update:to-page="newPage"
          is-dark
        >
          <template v-slot:day-content="{ day, attributes }">
            <div class="day-content">
              <div class="day-number">
                <span>{{ day.day }}</span>
              </div>
              <button
                v-for="attr in attributes"
                :key="attr.key"
                class="day-event"
                @click="openEventModal([attr.customData], day.date)"
              >
                {{ attr.customData.name }}
              </button>
            </div>
          </template>
        </v-calendar>
      </div>

      <div class="col-lg-4 py-4">
        <div class="events-wrapper">
          <button
            v-for="c in ordernedItems"
            class="event"
            :key="c.id"
            @click="openEventModal([c], '')"
          >
            {{ c.name }}
            <small>{{ toHumanDate(c.event_at, true) }}</small>
          </button>
        </div>
      </div>
    </div>

    <b-modal v-model="dialog" hide-header hide-footer centered>
      <button class="btn-close" @click="dialog = false">
        <i class="fa-regular fa-xmark fa-lg"></i>
      </button>
      <div v-for="(item, index) in items" :key="index" class="p-3 text-light">
        <h6>
          {{ item.name }}<br />
          <small class="text-secondary">{{
            toHumanDate(item.event_at, true)
          }}</small>
        </h6>
        <div v-html="item.description" />
        <button class="btn btn-primary btn-sm" @click="addToCalendar(item)">
          <i class="icon-plus"></i> Adicionar ao Google Agenda
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { toHumanDate, toCalendarDate } from "@/functions/helpers";
import { mapState } from "vuex";

export default {
  data() {
    return {
      attrs: [],
      loading: false,
      error: false,
      date: null,
      dialog: false,
      items: []
    };
  },
  computed: {
    ...mapState("notifications", ["calendar"]),
    ordernedItems() {
      let arr = [...this.calendar];
      arr.sort(this.compare);
      arr.reverse();
      return arr;
    }
  },
  methods: {
    addToCalendar(item) {
      const startDate = toCalendarDate(item.event_at);
      const finishDate = toCalendarDate(item.finished_at);
      const details = encodeURIComponent(item.description);
      const title = encodeURIComponent(item.name);
      const googleCalendarUrl = `https://www.google.com/calendar/event?action=TEMPLATE&dates=${startDate}/${finishDate}&text=${title}&details=${details}`;
      window.open(googleCalendarUrl, "_blank");
    },
    compare(a, b) {
      if (a.event_at > b.event_at) {
        return -1;
      }
      if (a.event_at < b.event_at) {
        return 1;
      } else {
        return 0;
      }
    },
    toHumanDate,
    openEventModal(items) {
      if (items && items.length) {
        this.items = items;
        this.dialog = true;
      }
    },
    clickNotification(n) {
      if (["broadcast", "direct"].includes(n.type)) {
        this.message = n.message;
        this.dialog = true;
      }
    },
    load() {
      this.loading = true;
      this.error = false;

      const month = `${this.date.month}`.padStart(2, "0");
      const lastDay = new Date(
        this.date.year,
        this.date.month,
        0,
        12
      ).getDate();
      const params = {
        event_at_after: `${this.date.year}-${month}-1`,
        event_at_before: `${this.date.year}-${month}-${lastDay}`
      };
      this.$store
        .dispatch("notifications/getCalendar", params)
        .then(() => {
          this.attrs = this.calendar.map(c => ({
            key: c.id,
            customData: { ...c },
            dates: new Date(c.event_at)
          }));
        })
        .catch(() => {
          this.error = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    newPage(date) {
      this.date = date;
      this.load();
    }
  }
};
</script>

<style lang="scss" scoped>
.tools-calendar {
  i {
    font-size: 24px;

    @media screen and (max-width: 575px) {
      font-size: 20px;
    }
  }

  .events-wrapper {
    @media screen and (min-width: 992px) {
      max-height: calc(100dvh - 210px);
      overflow-y: auto;
    }

    .event {
      padding: 8px;
      font-size: 14px;
      line-height: 16px;
      border: none;
      background-color: rgba(0, 0, 0, 0.1);
      text-align: left;
      display: block;
      color: white;
      width: 100%;
      margin-bottom: 8px;

      small {
        display: block;
        color: #bbb;
        font-size: 12px;
        line-height: 14px;
      }
    }
  }

  ::v-deep .vc-container {
    background-color: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    border: 1px solid rgba(255, 255, 255, 0.1);

    .vc-header {
      padding-bottom: 8px;
    }

    .vc-weeks {
      max-width: 100%;
      padding: 0;

      > * {
        min-width: 0;
      }

      .is-not-in-month * {
        opacity: 0.5;
      }

      .vc-weekday {
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        border-top: 1px solid rgba(255, 255, 255, 0.1);

        &:not(:first-child) {
          border-left: 1px solid rgba(255, 255, 255, 0.1);
        }
      }

      .vc-day {
        aspect-ratio: 1;
        overflow-y: scroll;

        @media screen and (max-width: 767px) {
          aspect-ratio: 0.5;
        }

        &.is-today {
          background-color: rgba(255, 255, 255, 0.1);
        }

        &:not(.on-bottom) {
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        &:not(.on-right) {
          border-right: 1px solid rgba(255, 255, 255, 0.1);
        }
      }
    }
  }

  .day-content {
    padding: 4px;

    .day-number {
      padding: 8px;
      display: flex;
      justify-content: flex-end;
    }

    .day-event {
      border-radius: 3px;
      background: #1b146c;
      color: white;
      border: none;
      padding: 2px;
      max-width: 100%;
      margin-bottom: 2px;
      text-align: left;
      overflow: hidden;

      @media screen and (min-width: 576px) {
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 11px;
      }

      @media screen and (max-width: 575px) {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        font-size: 9px;
      }
    }
  }
}
</style>
